import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, keyframes } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabaseClient';
import axios from 'axios';

// ── Keyframe animations ──────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  0% { transform: scale(0.3) rotate(-10deg); opacity: 0; }
  60% { transform: scale(1.1) rotate(2deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const floatUp = keyframes`
  0% { transform: translateY(40px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const sparkle = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 1; }
  50% { transform: scale(1) rotate(180deg); opacity: 1; }
  100% { transform: scale(0) rotate(360deg); opacity: 0; }
`;

const giftShake = keyframes`
  0%, 100% { transform: rotate(0deg); }
  10% { transform: rotate(-8deg); }
  20% { transform: rotate(8deg); }
  30% { transform: rotate(-6deg); }
  40% { transform: rotate(6deg); }
  50% { transform: rotate(-3deg); }
  60% { transform: rotate(3deg); }
  70% { transform: rotate(0deg); }
`;

const lidOpen = keyframes`
  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
  40% { transform: translateY(-60px) rotate(-15deg); opacity: 1; }
  100% { transform: translateY(-120px) rotate(-30deg); opacity: 0; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 30px rgba(102, 126, 234, 0.3), 0 0 60px rgba(118, 75, 162, 0.2); }
  50% { box-shadow: 0 0 50px rgba(102, 126, 234, 0.5), 0 0 100px rgba(118, 75, 162, 0.3); }
`;

const riseFromBox = keyframes`
  0% { transform: translateY(60px) scale(0.5); opacity: 0; }
  60% { transform: translateY(-10px) scale(1.05); opacity: 1; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
`;

// ── Sparkle particle component ───────────────────────────────────

const SparkleParticle = ({ delay, x, y, size, color }) => (
  <Box
    sx={{
      position: 'absolute',
      left: `${x}%`,
      top: `${y}%`,
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      animation: `${sparkle} 1.5s ease-out ${delay}s infinite`,
      pointerEvents: 'none',
    }}
  />
);

// ── Main component ───────────────────────────────────────────────

const GiftNotification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle | shaking | opening | revealed | exiting
  const [visible, setVisible] = useState(false);

  const getAuthHeader = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Fetch unread notifications on mount
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchNotifications = async () => {
      try {
        const headers = await getAuthHeader();
        const resp = await axios.get('/api/usage?notifications=1', { headers });
        const notifs = resp.data?.notifications || [];
        if (!cancelled && notifs.length > 0) {
          setNotifications(notifs);
          setCurrentIndex(0);
          setVisible(true);
          setPhase('shaking');
        }
      } catch {
        // Silently fail — notifications are non-critical
      }
    };

    // Small delay so the app loads first
    const timer = setTimeout(fetchNotifications, 1200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [user, getAuthHeader]);

  // Auto-advance from shaking to opening after 2s
  useEffect(() => {
    if (phase === 'shaking') {
      const timer = setTimeout(() => setPhase('opening'), 2000);
      return () => clearTimeout(timer);
    }
    if (phase === 'opening') {
      const timer = setTimeout(() => setPhase('revealed'), 800);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  const currentNotif = notifications[currentIndex];

  const handleDismiss = async () => {
    if (!currentNotif) return;
    setPhase('exiting');

    // Mark as read
    try {
      const headers = await getAuthHeader();
      await axios.post('/api/usage?notifications=1', { notificationId: currentNotif.id }, { headers });
    } catch {
      // Non-critical
    }

    setTimeout(() => {
      if (currentIndex + 1 < notifications.length) {
        setCurrentIndex((i) => i + 1);
        setPhase('shaking');
      } else {
        setVisible(false);
        setPhase('idle');
      }
    }, 400);
  };

  const handleGoToContent = async () => {
    if (!currentNotif) return;

    // Mark as read
    try {
      const headers = await getAuthHeader();
      await axios.post('/api/usage?notifications=1', { notificationId: currentNotif.id }, { headers });
    } catch {
      // Non-critical
    }

    setVisible(false);
    setPhase('idle');
    navigate(currentNotif.type === 'gift_pdf' ? '/library' : '/flashcards');
  };

  if (!visible || !currentNotif) return null;

  const isPdf = currentNotif.type === 'gift_pdf';
  const cardCount = currentNotif.metadata?.cardCount || 0;
  const deckName = currentNotif.metadata?.deckName || 'Flashcard Deck';
  const pdfName = currentNotif.metadata?.fileName || 'Document';

  // Generate sparkle particles
  const sparkles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    delay: Math.random() * 2,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: 4 + Math.random() * 8,
    color: ['#FFD700', '#FF6B6B', '#667eea', '#764ba2', '#48BB78', '#F6E05E'][i % 6],
  }));

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(ellipse at center, rgba(30, 20, 60, 0.95) 0%, rgba(10, 5, 30, 0.98) 100%)',
        animation: phase === 'exiting'
          ? `${fadeIn} 0.4s ease reverse forwards`
          : `${fadeIn} 0.6s ease forwards`,
        overflow: 'hidden',
      }}
    >
      {/* Sparkle particles */}
      {(phase === 'opening' || phase === 'revealed') && sparkles.map((s) => (
        <SparkleParticle key={s.id} {...s} />
      ))}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          maxWidth: 440,
          px: 3,
          textAlign: 'center',
        }}
      >
        {/* Gift box area */}
        <Box
          sx={{
            position: 'relative',
            width: 180,
            height: 180,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Gift box - shaking phase */}
          {(phase === 'shaking' || phase === 'opening') && (
            <Box
              sx={{
                fontSize: 120,
                lineHeight: 1,
                animation: phase === 'shaking'
                  ? `${giftShake} 0.8s ease-in-out infinite`
                  : `${lidOpen} 0.8s ease forwards`,
                cursor: phase === 'shaking' ? 'pointer' : 'default',
                filter: 'drop-shadow(0 0 30px rgba(102, 126, 234, 0.5))',
                userSelect: 'none',
              }}
              onClick={() => phase === 'shaking' && setPhase('opening')}
            >
              🎁
            </Box>
          )}

          {/* Revealed content */}
          {phase === 'revealed' && (
            <Box
              sx={{
                animation: `${riseFromBox} 0.8s ease-out forwards`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  fontSize: 80,
                  lineHeight: 1,
                  mb: 1,
                  filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 0.6))',
                }}
              >
                {isPdf ? '📄' : '🃏'}
              </Box>
            </Box>
          )}
        </Box>

        {/* Title text */}
        {phase === 'shaking' && (
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: '#FFD700',
              animation: `${floatUp} 0.6s ease-out`,
              textShadow: '0 0 20px rgba(255, 215, 0, 0.4)',
              letterSpacing: 1,
            }}
          >
            You received a gift!
          </Typography>
        )}

        {phase === 'shaking' && (
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.6)',
              animation: `${floatUp} 0.8s ease-out`,
              fontSize: '0.95rem',
            }}
          >
            Tap the gift to open it
          </Typography>
        )}

        {/* Revealed info */}
        {phase === 'revealed' && (
          <>
            <Box
              sx={{
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: 3,
                px: 4,
                py: 3,
                animation: `${scaleIn} 0.6s ease-out, ${glowPulse} 2s ease-in-out 0.6s infinite`,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #FFD700, #FFA500, #FFD700)',
                  backgroundSize: '200% auto',
                  animation: `${shimmer} 3s linear infinite`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                {isPdf ? pdfName : deckName}
              </Typography>
              {!isPdf && (
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    color: '#fff',
                    textShadow: '0 0 30px rgba(102, 126, 234, 0.5)',
                    animation: `${pulse} 2s ease-in-out infinite`,
                  }}
                >
                  {cardCount} Cards
                </Typography>
              )}
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                animation: `${floatUp} 0.8s ease-out 0.3s both`,
                maxWidth: 320,
              }}
            >
              {isPdf
                ? 'A new document has been added to your library!'
                : 'A new flashcard deck has been added to your collection!'}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mt: 1,
                animation: `${floatUp} 0.8s ease-out 0.5s both`,
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={handleGoToContent}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    boxShadow: '0 6px 30px rgba(102, 126, 234, 0.6)',
                  },
                }}
              >
                {isPdf ? 'View My Library' : 'View My Cards'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleDismiss}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  borderColor: 'rgba(255,255,255,0.3)',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.5)',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                Later
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default GiftNotification;
